import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";

const API = `${window.location.origin}`;

export default function LoginPage() {
  const { user, login, loading } = useAuth();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const token = searchParams.get("token");
    if (token) {
      login(token);
      window.history.replaceState({}, "", "/login");
    }
  }, [searchParams, login]);

  useEffect(() => {
    if (user && !loading) {
      window.location.href = "/";
    }
  }, [user, loading]);

  const error = searchParams.get("error");

  const handleLogin = () => {
    window.location.href = `${API}/api/auth/jumpcloud/login`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f5f5]">
        <div className="animate-spin h-8 w-8 border-2 border-slate-300 border-t-slate-700 rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center relative"
      style={{
        backgroundImage: `url('https://images.unsplash.com/photo-1760875506684-83ba574f64ce?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1920')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="absolute inset-0 bg-black/50" />
      <div className="w-full max-w-sm relative z-10">
        <div className="bg-white rounded-2xl shadow-lg p-10">
          <div className="flex justify-center mb-6">
            <img src="/abs-logo.png" alt="ABS" className="h-20 w-auto" />
          </div>

          <h1 className="text-2xl font-bold text-center text-slate-900 mb-1">Pipeline</h1>
          <p className="text-sm text-slate-500 text-center mb-8">Sign in to continue</p>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700" data-testid="login-error">
              {error === "auth_failed" && "Authentication failed. Please try again."}
              {error === "token_exchange_failed" && "Login failed. Please contact your admin."}
              {error === "invalid_state" && "Session expired. Please try again."}
              {error === "server_error" && "Server error. Please try again later."}
              {!["auth_failed", "token_exchange_failed", "invalid_state", "server_error"].includes(error) && "An error occurred."}
            </div>
          )}

          <button
            onClick={handleLogin}
            data-testid="jumpcloud-login-btn"
            className="w-full flex items-center justify-center gap-3 px-4 py-4 bg-[#4D6CFA] text-white text-sm font-semibold rounded-lg hover:bg-[#3b5ae8] transition-colors"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            Sign in with JumpCloud
          </button>

          <p className="text-xs text-slate-400 text-center mt-6">
            Access restricted to @absinc.com email addresses
          </p>
        </div>
      </div>

      <div className="flex items-center gap-1.5 mt-6 relative z-10 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full shadow-sm">
        <svg className="h-4 w-4 text-green-500" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
        </svg>
        <span className="text-xs text-green-700 font-medium">Secured with OpenID Connect</span>
      </div>
    </div>
  );
}
